import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import SearchableSelect from '../components/SearchableSelect';

/* ── All 64 voucher template records from Tally LLP ── */
const TALLY_GUIDE_DATA = [
  {
    scenario_category: "Receipts",
    scenario: "Capital introduced",
    ledger_name: "Partner Capital – Partner A",
    tally_group: "Capital Account",
    ledger_code: "300.001",
    voucher_type: "Receipt",
    debit_ledgers: "Bank",
    credit_ledgers: "Partner Capital – Partner A",
    narration_example: "Capital introduced",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank/Cash A/c | 1,000 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "Payments",
    scenario: "Capital withdrawn",
    ledger_name: "Partner Capital – Partner A",
    tally_group: "Capital Account",
    ledger_code: "300.001",
    voucher_type: "Payment",
    debit_ledgers: "Partner Capital – Partner A",
    credit_ledgers: "Bank",
    narration_example: "Capital withdrawal",
    notes: "Prefer Partner Current for drawings",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c | 1,000 | \nAsset | Bank/Cash A/c |  | 1,000"
  },
  {
    scenario_category: "Payments",
    scenario: "Partner drawings/payout",
    ledger_name: "Partner Current Account – Partner A",
    tally_group: "Capital Account",
    ledger_code: "300.002",
    voucher_type: "Payment",
    debit_ledgers: "Partner Current Account – Partner A",
    credit_ledgers: "Bank",
    narration_example: "Partner payout",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c | 1,000 | \nAsset | Bank/Cash A/c |  | 1,000"
  },
  {
    scenario_category: "Adjustments / Provisions",
    scenario: "Profit allocation",
    ledger_name: "Partner Current Account – Partner A",
    tally_group: "Capital Account",
    ledger_code: "300.002",
    voucher_type: "Journal",
    debit_ledgers: "Profit & Loss A/c",
    credit_ledgers: "Partner Current Account – Partner A",
    narration_example: "Profit allocation",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nCapital | Profit & Loss A/c | 1,000 | \nCapital | Partner Current / Retained Earnings |  | 1,000"
  },
  {
    scenario_category: "Receipts",
    scenario: "Loan received",
    ledger_name: "Term Loan – Bank",
    tally_group: "Loans (Liability)",
    ledger_code: "200.001",
    voucher_type: "Receipt",
    debit_ledgers: "Bank",
    credit_ledgers: "Term Loan – Bank",
    narration_example: "Loan received",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank/Cash A/c | 1,000 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "Payments",
    scenario: "Principal repayment",
    ledger_name: "Term Loan – Bank",
    tally_group: "Loans (Liability)",
    ledger_code: "200.001",
    voucher_type: "Payment",
    debit_ledgers: "Term Loan – Bank",
    credit_ledgers: "Bank",
    narration_example: "Loan repayment",
    notes: "Interest goes to Interest on Loans",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c | 1,000 | \nAsset | Bank/Cash A/c |  | 1,000"
  },
  {
    scenario_category: "GST",
    scenario: "Book vendor bill (with GST if applicable)",
    ledger_name: "Vendors – Professional Services",
    tally_group: "Sundry Creditors",
    ledger_code: "210.001",
    voucher_type: "Purchase",
    debit_ledgers: "Expense/Asset + Input GST",
    credit_ledgers: "Vendors – Professional Services",
    narration_example: "Vendor bill booked",
    notes: "Input GST if eligible",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c (Taxable Value) | 1,000 | \nAsset | Input CGST @9% | 90 | \nAsset | Input SGST @9% | 90 | \nLiability | Vendor / Supplier Payable |  | 1,180"
  },
  {
    scenario_category: "TDS",
    scenario: "Deduct TDS on vendor bill",
    ledger_name: "Vendors – Professional Services",
    tally_group: "Sundry Creditors",
    ledger_code: "210.001",
    voucher_type: "Journal",
    debit_ledgers: "Vendors – Professional Services",
    credit_ledgers: "TDS Payable – Professional/Contract",
    narration_example: "TDS deduction",
    notes: "As per section/rate",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | Vendor / Supplier Payable | 100 | \nLiability | TDS Payable A/c |  | 100"
  },
  {
    scenario_category: "TDS",
    scenario: "Pay vendor (net of TDS)",
    ledger_name: "Vendors – Professional Services",
    tally_group: "Sundry Creditors",
    ledger_code: "210.001",
    voucher_type: "Payment",
    debit_ledgers: "Vendors – Professional Services",
    credit_ledgers: "Bank",
    narration_example: "Vendor payment",
    notes: "Pay net amount",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c (Gross) | 1,000 | \nLiability | TDS Payable A/c (10% TDS) |  | 100\nAsset | Bank A/c (Net Paid) |  | 900"
  },
  {
    scenario_category: "Receipts",
    scenario: "Receive customer advance",
    ledger_name: "Advance from Customers",
    tally_group: "Sundry Creditors",
    ledger_code: "210.002",
    voucher_type: "Receipt",
    debit_ledgers: "Bank / Cash",
    credit_ledgers: "Advance from Customers",
    narration_example: "Advance received",
    notes: "Adjust later against invoice",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank/Cash A/c | 1,000 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "Sales",
    scenario: "Adjust advance against invoice",
    ledger_name: "Advance from Customers",
    tally_group: "Sundry Creditors",
    ledger_code: "210.002",
    voucher_type: "Journal",
    debit_ledgers: "Advance from Customers",
    credit_ledgers: "Customer Ledger",
    narration_example: "Advance adjustment",
    notes: "Adjust at invoicing",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Journal Debit | 1,000 | \nLiability | Journal Credit |  | 1,000"
  },
  {
    scenario_category: "GST",
    scenario: "Output GST created on sales invoice",
    ledger_name: "Output CGST",
    tally_group: "Duties & Taxes",
    ledger_code: "220.001",
    voucher_type: "Sales",
    debit_ledgers: "Customer",
    credit_ledgers: "Service Income + Output CGST",
    narration_example: "GST on sales",
    notes: "Auto if configured",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Customer – Domestic Debtors | 1,180 | \nIncome | Sales / Service Income |  | 1,000\nLiability | Output CGST @9% |  | 90\nLiability | Output SGST @9% |  | 90"
  },
  {
    scenario_category: "GST",
    scenario: "GST payment to govt",
    ledger_name: "Output CGST",
    tally_group: "Duties & Taxes",
    ledger_code: "220.001",
    voucher_type: "Payment",
    debit_ledgers: "Output CGST",
    credit_ledgers: "Bank",
    narration_example: "GST payment",
    notes: "Pay net of ITC",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | Output GST Payable | 1,000 | \nAsset | Bank A/c |  | 1,000"
  },
  {
    scenario_category: "GST",
    scenario: "Input GST on vendor bill",
    ledger_name: "Input CGST",
    tally_group: "Duties & Taxes",
    ledger_code: "220.002",
    voucher_type: "Purchase",
    debit_ledgers: "Expense/Asset + Input CGST",
    credit_ledgers: "Vendor",
    narration_example: "ITC booked",
    notes: "Ensure eligible ITC",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c (Taxable Value) | 1,000 | \nAsset | Input CGST @9% | 90 | \nAsset | Input SGST @9% | 90 | \nLiability | Vendor / Supplier Payable |  | 1,180"
  },
  {
    scenario_category: "Adjustments / Provisions",
    scenario: "ITC reversal/blocked credit",
    ledger_name: "Input CGST",
    tally_group: "Duties & Taxes",
    ledger_code: "220.002",
    voucher_type: "Journal",
    debit_ledgers: "Expense (Blocked ITC)",
    credit_ledgers: "Input CGST",
    narration_example: "ITC reversal",
    notes: "Reverse ineligible ITC",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Journal Debit | 1,000 | \nLiability | Journal Credit |  | 1,000"
  },
  {
    scenario_category: "TDS",
    scenario: "TDS deducted from vendor",
    ledger_name: "TDS Payable – Professional",
    tally_group: "Duties & Taxes",
    ledger_code: "220.003",
    voucher_type: "Journal",
    debit_ledgers: "Vendor",
    credit_ledgers: "TDS Payable – Professional",
    narration_example: "TDS payable created",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | Vendor / Supplier Payable | 100 | \nLiability | TDS Payable A/c |  | 100"
  },
  {
    scenario_category: "TDS",
    scenario: "TDS challan payment",
    ledger_name: "TDS Payable – Professional",
    tally_group: "Duties & Taxes",
    ledger_code: "220.003",
    voucher_type: "Payment",
    debit_ledgers: "TDS Payable – Professional",
    credit_ledgers: "Bank",
    narration_example: "TDS payment",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | TDS Payable A/c | 1,000 | \nAsset | Bank A/c |  | 1,000"
  },
  {
    scenario_category: "Receipts",
    scenario: "Receipt of money",
    ledger_name: "Axis Bank – Current Account",
    tally_group: "Bank Accounts",
    ledger_code: "110.001",
    voucher_type: "Receipt",
    debit_ledgers: "Axis Bank – Current Account",
    credit_ledgers: "Customer/Income/Loan/Partner",
    narration_example: "Receipt voucher",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank/Cash A/c | 1,000 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "Payments",
    scenario: "Payment of money",
    ledger_name: "Axis Bank – Current Account",
    tally_group: "Bank Accounts",
    ledger_code: "110.001",
    voucher_type: "Payment",
    debit_ledgers: "Expense/Vendor/Asset",
    credit_ledgers: "Axis Bank – Current Account",
    narration_example: "Payment voucher",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | Vendor / Supplier Payable | 1,000 | \nAsset | Bank/Cash A/c |  | 1,000"
  },
  {
    scenario_category: "Bank / Cash Transfers",
    scenario: "Bank-to-bank transfer",
    ledger_name: "Axis Bank – Current Account",
    tally_group: "Bank Accounts",
    ledger_code: "110.001",
    voucher_type: "Contra",
    debit_ledgers: "Other Bank",
    credit_ledgers: "Axis Bank – Current Account",
    narration_example: "Contra transfer",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank A/c | 1,000 | \nAsset | Cash/Other Bank A/c |  | 1,000"
  },
  {
    scenario_category: "Receipts",
    scenario: "Cash receipt",
    ledger_name: "Cash",
    tally_group: "Cash-in-Hand",
    ledger_code: "111.001",
    voucher_type: "Receipt",
    debit_ledgers: "Cash",
    credit_ledgers: "Customer/Income/Partner",
    narration_example: "Cash received",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank/Cash A/c | 1,000 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "Payments",
    scenario: "Cash payment (petty)",
    ledger_name: "Cash",
    tally_group: "Cash-in-Hand",
    ledger_code: "111.001",
    voucher_type: "Payment",
    debit_ledgers: "Expense",
    credit_ledgers: "Cash",
    narration_example: "Petty cash paid",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c | 1,000 | \nAsset | Bank/Cash A/c |  | 1,000"
  },
  {
    scenario_category: "Bank / Cash Transfers",
    scenario: "Cash deposit/withdrawal",
    ledger_name: "Cash",
    tally_group: "Cash-in-Hand",
    ledger_code: "111.001",
    voucher_type: "Contra",
    debit_ledgers: "Bank/Cash",
    credit_ledgers: "Cash/Bank",
    narration_example: "Contra cash-bank",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank A/c | 1,000 | \nAsset | Cash/Other Bank A/c |  | 1,000"
  },
  {
    scenario_category: "GST",
    scenario: "Raise domestic sales invoice (GST)",
    ledger_name: "Customers – Domestic",
    tally_group: "Sundry Debtors",
    ledger_code: "120.001",
    voucher_type: "Sales",
    debit_ledgers: "Customers – Domestic",
    credit_ledgers: "Service Income + Output GST",
    narration_example: "Domestic sales invoice",
    notes: "Output tax as applicable",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Customer – Domestic Debtors | 1,180 | \nIncome | Sales / Service Income |  | 1,000\nLiability | Output CGST @9% |  | 90\nLiability | Output SGST @9% |  | 90"
  },
  {
    scenario_category: "TDS",
    scenario: "Receive customer payment (net/TDS)",
    ledger_name: "Customers – Domestic",
    tally_group: "Sundry Debtors",
    ledger_code: "120.001",
    voucher_type: "Receipt",
    debit_ledgers: "Bank (net) + TDS Receivable (if any)",
    credit_ledgers: "Customers – Domestic",
    narration_example: "Customer receipt",
    notes: "Split if TDS deducted",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank A/c (Net Received) | 900 | \nAsset | TDS Receivable A/c (10% TDS) | 100 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "General",
    scenario: "Issue credit note (domestic)",
    ledger_name: "Customers – Domestic",
    tally_group: "Sundry Debtors",
    ledger_code: "120.001",
    voucher_type: "Credit Note",
    debit_ledgers: "Service Income + Output GST",
    credit_ledgers: "Customers – Domestic",
    narration_example: "Credit note",
    notes: "Use Credit Note",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Customers – Domestic | 1,000 | \nAsset | Counter A/c |  | 1,000"
  },
  {
    scenario_category: "TDS",
    scenario: "TDS deducted by customer on receipt",
    ledger_name: "TDS Receivable",
    tally_group: "Current Assets",
    ledger_code: "130.001",
    voucher_type: "Receipt",
    debit_ledgers: "Bank (net) + TDS Receivable",
    credit_ledgers: "Customer",
    narration_example: "Receipt with TDS",
    notes: "Split receipt",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank A/c (Net Received) | 900 | \nAsset | TDS Receivable A/c (10% TDS) | 100 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "TDS",
    scenario: "Set-off TDS at tax time",
    ledger_name: "TDS Receivable",
    tally_group: "Current Assets",
    ledger_code: "130.001",
    voucher_type: "Journal",
    debit_ledgers: "Income Tax / Advance Tax",
    credit_ledgers: "TDS Receivable",
    narration_example: "TDS setoff",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | Vendor / Supplier Payable | 100 | \nLiability | TDS Payable A/c |  | 100"
  },
  {
    scenario_category: "Adjustments / Provisions",
    scenario: "Accrue revenue not yet billed",
    ledger_name: "Unbilled Revenue (WIP)",
    tally_group: "Current Assets",
    ledger_code: "130.002",
    voucher_type: "Journal",
    debit_ledgers: "Unbilled Revenue (WIP)",
    credit_ledgers: "Service Income",
    narration_example: "Revenue accrual",
    notes: "Reverse on invoicing",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Unbilled Revenue (WIP) | 1,000 | \nIncome | Service Income |  | 1,000"
  },
  {
    scenario_category: "Adjustments / Provisions",
    scenario: "Reverse WIP on invoicing",
    ledger_name: "Unbilled Revenue (WIP)",
    tally_group: "Current Assets",
    ledger_code: "130.002",
    voucher_type: "Journal",
    debit_ledgers: "Customer",
    credit_ledgers: "Unbilled Revenue (WIP)",
    narration_example: "WIP reversal",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Customer / Debtors A/c | 1,000 | \nAsset | Unbilled Revenue (WIP) |  | 1,000"
  },
  {
    scenario_category: "Purchases",
    scenario: "Asset purchase",
    ledger_name: "Computers & Laptops",
    tally_group: "Fixed Assets",
    ledger_code: "150.001",
    voucher_type: "Purchase",
    debit_ledgers: "Computers & Laptops",
    credit_ledgers: "Bank/Vendor",
    narration_example: "Fixed asset bought",
    notes: "Add Input GST if eligible",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense / Purchase A/c | 1,000 | \nLiability | Vendor / Supplier Payable |  | 1,000"
  },
  {
    scenario_category: "Receipts",
    scenario: "Asset sale",
    ledger_name: "Computers & Laptops",
    tally_group: "Fixed Assets",
    ledger_code: "150.001",
    voucher_type: "Receipt",
    debit_ledgers: "Bank/Customer",
    credit_ledgers: "Computers & Laptops",
    narration_example: "Fixed asset sold",
    notes: "Also book profit/loss",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank/Cash A/c | 1,000 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "Adjustments / Provisions",
    scenario: "Monthly depreciation posting",
    ledger_name: "Accumulated Depreciation",
    tally_group: "Fixed Assets",
    ledger_code: "150.002",
    voucher_type: "Journal",
    debit_ledgers: "Depreciation",
    credit_ledgers: "Accumulated Depreciation",
    narration_example: "Depreciation",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Depreciation | 1,000 | \nAsset | Accumulated Depreciation |  | 1,000"
  },
  {
    scenario_category: "Adjustments / Provisions",
    scenario: "Asset disposal adjustment",
    ledger_name: "Accumulated Depreciation",
    tally_group: "Fixed Assets",
    ledger_code: "150.002",
    voucher_type: "Journal",
    debit_ledgers: "Accumulated Depreciation",
    credit_ledgers: "Asset/Profit/Loss on sale",
    narration_example: "Disposal",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Journal Debit | 1,000 | \nLiability | Journal Credit |  | 1,000"
  },
  {
    scenario_category: "Sales",
    scenario: "Recognise income via invoice",
    ledger_name: "IT Consulting Services",
    tally_group: "Direct Income",
    ledger_code: "410.001",
    voucher_type: "Sales",
    debit_ledgers: "Customer",
    credit_ledgers: "IT Consulting Services",
    narration_example: "Income recognised",
    notes: "Add Output GST for domestic",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Customer / Debtors A/c | 1,000 | \nIncome | Sales / Service Income |  | 1,000"
  },
  {
    scenario_category: "General",
    scenario: "Income reversal (credit note)",
    ledger_name: "IT Consulting Services",
    tally_group: "Direct Income",
    ledger_code: "410.001",
    voucher_type: "Credit Note",
    debit_ledgers: "IT Consulting Services",
    credit_ledgers: "Customer",
    narration_example: "Credit note",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | IT Consulting Services | 1,000 | \nAsset | Counter A/c |  | 1,000"
  },
  {
    scenario_category: "Sales",
    scenario: "Recognise income via invoice",
    ledger_name: "Interest Income",
    tally_group: "Indirect Income",
    ledger_code: "420.001",
    voucher_type: "Sales",
    debit_ledgers: "Customer",
    credit_ledgers: "Interest Income",
    narration_example: "Income recognised",
    notes: "Add Output GST for domestic",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Customer / Debtors A/c | 1,000 | \nIncome | Sales / Service Income |  | 1,000"
  },
  {
    scenario_category: "General",
    scenario: "Income reversal (credit note)",
    ledger_name: "Interest Income",
    tally_group: "Indirect Income",
    ledger_code: "420.001",
    voucher_type: "Credit Note",
    debit_ledgers: "Interest Income",
    credit_ledgers: "Customer",
    narration_example: "Credit note",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Interest Income | 1,000 | \nAsset | Counter A/c |  | 1,000"
  },
  {
    scenario_category: "Purchases",
    scenario: "Book expense bill",
    ledger_name: "Salaries – Billable Staff",
    tally_group: "Direct Expenses",
    ledger_code: "510.001",
    voucher_type: "Purchase",
    debit_ledgers: "Salaries – Billable Staff",
    credit_ledgers: "Vendor",
    narration_example: "Expense booked",
    notes: "Input GST/TDS if applicable",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense / Purchase A/c | 1,000 | \nLiability | Vendor / Supplier Payable |  | 1,000"
  },
  {
    scenario_category: "Payments",
    scenario: "Pay expense directly",
    ledger_name: "Salaries – Billable Staff",
    tally_group: "Direct Expenses",
    ledger_code: "510.001",
    voucher_type: "Payment",
    debit_ledgers: "Salaries – Billable Staff",
    credit_ledgers: "Bank/Cash",
    narration_example: "Expense paid",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c | 1,000 | \nAsset | Bank/Cash A/c |  | 1,000"
  },
  {
    scenario_category: "Purchases",
    scenario: "Book expense bill",
    ledger_name: "Partner Remuneration",
    tally_group: "Indirect Expenses",
    ledger_code: "610.001",
    voucher_type: "Purchase",
    debit_ledgers: "Partner Remuneration",
    credit_ledgers: "Vendor",
    narration_example: "Expense booked",
    notes: "Input GST/TDS if applicable",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense / Purchase A/c | 1,000 | \nLiability | Vendor / Supplier Payable |  | 1,000"
  },
  {
    scenario_category: "Payments",
    scenario: "Pay expense directly",
    ledger_name: "Partner Remuneration",
    tally_group: "Indirect Expenses",
    ledger_code: "610.001",
    voucher_type: "Payment",
    debit_ledgers: "Partner Remuneration",
    credit_ledgers: "Bank/Cash",
    narration_example: "Expense paid",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c | 1,000 | \nAsset | Bank/Cash A/c |  | 1,000"
  },
  {
    scenario_category: "Purchases",
    scenario: "Book expense bill",
    ledger_name: "Office Rent",
    tally_group: "Indirect Expenses",
    ledger_code: "610.002",
    voucher_type: "Purchase",
    debit_ledgers: "Office Rent",
    credit_ledgers: "Vendor",
    narration_example: "Expense booked",
    notes: "Input GST/TDS if applicable",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense / Purchase A/c | 1,000 | \nLiability | Vendor / Supplier Payable |  | 1,000"
  },
  {
    scenario_category: "Payments",
    scenario: "Pay expense directly",
    ledger_name: "Office Rent",
    tally_group: "Indirect Expenses",
    ledger_code: "610.002",
    voucher_type: "Payment",
    debit_ledgers: "Office Rent",
    credit_ledgers: "Bank/Cash",
    narration_example: "Expense paid",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c | 1,000 | \nAsset | Bank/Cash A/c |  | 1,000"
  },
  {
    scenario_category: "Bank Charges",
    scenario: "Bank charges as per statement",
    ledger_name: "Bank Charges",
    tally_group: "Indirect Expenses",
    ledger_code: "610.003",
    voucher_type: "Journal",
    debit_ledgers: "Bank Charges",
    credit_ledgers: "Bank",
    narration_example: "Bank charges",
    notes: "",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Bank Charges | 1,000 | \nAsset | Bank A/c |  | 1,000"
  },
  {
    scenario_category: "GST",
    scenario: "Domestic Sales Invoice (GST) – Detailed (Intra‑state: CGST+SGST)",
    ledger_name: "Customers – Domestic",
    tally_group: "Sundry Debtors",
    ledger_code: "120.001",
    voucher_type: "Sales",
    debit_ledgers: "Customers – Domestic (Invoice Total)",
    credit_ledgers: "IT Consulting Services / relevant Service Income (Taxable Value)\nOutput CGST (CGST Amount)\nOutput SGST (SGST Amount)",
    narration_example: "Invoice raised – intra‑state sale with GST",
    notes: "In Sales voucher, select CGST & SGST ledgers for intra‑state supplies.",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Customer – Domestic Debtors | 1,180 | \nIncome | Sales / Service Income |  | 1,000\nLiability | Output CGST @9% |  | 90\nLiability | Output SGST @9% |  | 90"
  },
  {
    scenario_category: "GST",
    scenario: "Domestic Sales Invoice (GST) – Detailed (Inter‑state: IGST)",
    ledger_name: "Customers – Domestic",
    tally_group: "Sundry Debtors",
    ledger_code: "120.001",
    voucher_type: "Sales",
    debit_ledgers: "Customers – Domestic (Invoice Total)",
    credit_ledgers: "IT Consulting Services / relevant Service Income (Taxable Value)\nOutput IGST (IGST Amount)",
    narration_example: "Invoice raised – inter‑state sale with IGST",
    notes: "In Sales voucher, select IGST ledger for inter‑state supplies.",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Customer – Inter‑State Debtors | 1,180 | \nIncome | Sales / Service Income |  | 1,000\nLiability | Output IGST @18% |  | 180"
  },
  {
    scenario_category: "GST",
    scenario: "Purchase / Expense Bill (GST) – Detailed (Intra‑state: Input CGST+SGST)",
    ledger_name: "Vendors – Professional Services",
    tally_group: "Sundry Creditors",
    ledger_code: "210.001",
    voucher_type: "Purchase",
    debit_ledgers: "Relevant Expense / Asset (Taxable Value)\nInput CGST (CGST Amount)\nInput SGST (SGST Amount)",
    credit_ledgers: "Vendors – Professional Services (Invoice Total)",
    narration_example: "Vendor bill booked – intra‑state with GST",
    notes: "In Purchase voucher, select Input CGST & Input SGST ledgers if ITC eligible.",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c (Taxable Value) | 1,000 | \nAsset | Input CGST @9% | 90 | \nAsset | Input SGST @9% | 90 | \nLiability | Vendor / Supplier Payable |  | 1,180"
  },
  {
    scenario_category: "GST",
    scenario: "Purchase / Expense Bill (GST) – Detailed (Inter‑state: Input IGST)",
    ledger_name: "Vendors – Professional Services",
    tally_group: "Sundry Creditors",
    ledger_code: "210.001",
    voucher_type: "Purchase",
    debit_ledgers: "Relevant Expense / Asset (Taxable Value)\nInput IGST (IGST Amount)",
    credit_ledgers: "Vendors – Professional Services (Invoice Total)",
    narration_example: "Vendor bill booked – inter‑state with IGST",
    notes: "In Purchase voucher, select Input IGST ledger if ITC eligible.",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c (Taxable Value) | 1,000 | \nAsset | Input IGST @18% | 180 | \nLiability | Vendor / Supplier Payable |  | 1,180"
  },
  {
    scenario_category: "TDS",
    scenario: "Vendor Payment with TDS Deducted (Outward) – Detailed Split",
    ledger_name: "Vendors – Professional Services",
    tally_group: "Sundry Creditors",
    ledger_code: "210.001",
    voucher_type: "Payment",
    debit_ledgers: "Vendors – Professional Services (Gross Payable)",
    credit_ledgers: "Axis Bank – Current Account (Net Paid)\nTDS Payable – Professional / Contract (TDS Amount)",
    narration_example: "Vendor paid net after TDS deduction",
    notes: "Tally supports deducting TDS via Journal and then paying net via Payment voucher. See TDS transaction workflow in Tally documentation.",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Expense A/c (Gross) | 1,000 | \nLiability | TDS Payable A/c (10% TDS) |  | 100\nAsset | Bank A/c (Net Paid) |  | 900"
  },
  {
    scenario_category: "TDS",
    scenario: "Customer Receipt with TDS Deducted (Inward) – Detailed Split",
    ledger_name: "Customers – Domestic",
    tally_group: "Sundry Debtors",
    ledger_code: "120.001",
    voucher_type: "Receipt",
    debit_ledgers: "Axis Bank – Current Account (Net Received)\nTDS Receivable (TDS Amount)",
    credit_ledgers: "Customers – Domestic (Gross Receipt / Invoice Amount)",
    narration_example: "Customer paid net and deducted TDS",
    notes: "Split receipt into Bank (net) + TDS Receivable to clear invoice properly.",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank A/c (Net Received) | 900 | \nAsset | TDS Receivable A/c (10% TDS) | 100 | \nAsset | Customer / Debtors A/c |  | 1,000"
  },
  {
    scenario_category: "TDS",
    scenario: "Deposit TDS to Government",
    ledger_name: "TDS Payable – Professional",
    tally_group: "Duties & Taxes",
    ledger_code: "220.003",
    voucher_type: "Payment",
    debit_ledgers: "TDS Payable – Professional / Contract",
    credit_ledgers: "Axis Bank – Current Account",
    narration_example: "TDS challan payment",
    notes: "Pay TDS payable to govt using Payment voucher.",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | TDS Payable A/c | 1,000 | \nAsset | Bank A/c |  | 1,000"
  },
  {
    scenario_category: "Bank Charges",
    scenario: "Bank charges deducted on outgoing payment (bank fee ₹10 on ₹1,000 transfer)",
    ledger_name: "Bank Charges",
    tally_group: "Indirect Expenses",
    ledger_code: "610.003",
    voucher_type: "Payment",
    debit_ledgers: "Vendor/Expense (₹990) + Bank Charges (₹10)",
    credit_ledgers: "Axis Bank – Current Account (₹1,000)",
    narration_example: "Bank fee deducted on payment",
    notes: "Illustration uses ₹10 fee",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Bank Charges | 10 | \nAsset | Bank A/c |  | 10"
  },
  {
    scenario_category: "Bank Charges",
    scenario: "Bank charges deducted on incoming receipt (bank fee ₹10 on ₹1,000 receipt)",
    ledger_name: "Bank Charges",
    tally_group: "Indirect Expenses",
    ledger_code: "610.003",
    voucher_type: "Receipt",
    debit_ledgers: "Axis Bank – Current Account (₹990) + Bank Charges (₹10)",
    credit_ledgers: "Customer / Debtors (₹1,000)",
    narration_example: "Bank fee deducted on receipt",
    notes: "Illustration uses ₹10 fee",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Bank Charges | 10 | \nAsset | Bank A/c |  | 10"
  },
  {
    scenario_category: "Export / Zero-rated",
    scenario: "Exchange rate difference – Forex Gain on export receipt",
    ledger_name: "Foreign Exchange Gain",
    tally_group: "Indirect Income",
    ledger_code: "420.9",
    voucher_type: "Receipt",
    debit_ledgers: "Axis Bank – Current Account (₹1,020)",
    credit_ledgers: "Customers – Export (₹1,000) + Foreign Exchange Gain (₹20)",
    narration_example: "Forex gain on receipt",
    notes: "Invoice 1000, bank 1020",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Bank / EEFC A/c | 1,000 | \nAsset | Customer – Export Debtors |  | 1,000"
  },
  {
    scenario_category: "Forex / Exchange Difference",
    scenario: "Exchange rate difference – Forex Loss on foreign payment",
    ledger_name: "Foreign Exchange Loss",
    tally_group: "Indirect Expenses",
    ledger_code: "610.901",
    voucher_type: "Payment",
    debit_ledgers: "Vendor Payable (₹1,000) + Foreign Exchange Loss (₹20)",
    credit_ledgers: "Axis Bank – Current Account (₹1,020)",
    narration_example: "Forex loss on payment",
    notes: "Payable 1000, bank 1020",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Foreign Exchange Loss | 20 | \nLiability | Vendor Payable | 1,000 | \nAsset | Bank A/c |  | 1,020"
  },
  {
    scenario_category: "Payroll – Contractors",
    scenario: "Employee salary booking with PF (employee 12%) and Professional Tax ₹20",
    ledger_name: "Salaries – Billable Staff",
    tally_group: "Direct Expenses",
    ledger_code: "510.001",
    voucher_type: "Journal",
    debit_ledgers: "Salaries Expense (Gross ₹1,000)",
    credit_ledgers: "Salary Payable (₹860) + PF Payable – Employee (₹120) + Professional Tax Payable (₹20)",
    narration_example: "Salary JV",
    notes: "PF 12% + PT 20 example",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Salaries Expense (Gross) | 1,000 | \nLiability | PF Payable – Employee (12%) |  | 120\nLiability | Professional Tax Payable |  | 20\nLiability | Salary Payable (Net) |  | 860"
  },
  {
    scenario_category: "Payroll – Employees",
    scenario: "Employer PF contribution booking (company PF 12% of ₹1,000)",
    ledger_name: "Employer PF Expense",
    tally_group: "Indirect Expenses",
    ledger_code: "610.905",
    voucher_type: "Journal",
    debit_ledgers: "Employer PF Expense (₹120)",
    credit_ledgers: "PF Payable – Employer (₹120)",
    narration_example: "Employer PF JV",
    notes: "PF employer 12%",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Employer PF Expense (12%) | 120 | \nLiability | PF Payable – Employer |  | 120"
  },
  {
    scenario_category: "Payroll – Employees",
    scenario: "Salary payment to employee (net salary ₹860)",
    ledger_name: "Salary Payable",
    tally_group: "Current Liabilities",
    ledger_code: "230.901",
    voucher_type: "Payment",
    debit_ledgers: "Salary Payable (₹860)",
    credit_ledgers: "Axis Bank – Current Account (₹860)",
    narration_example: "Salary paid",
    notes: "Net after deductions",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | Salary Payable | 860 | \nAsset | Bank A/c |  | 860"
  },
  {
    scenario_category: "Payroll – Employees",
    scenario: "PF payment to PF authority (employee+employer combined)",
    ledger_name: "Provident Fund Payable",
    tally_group: "Duties & Taxes",
    ledger_code: "220.904",
    voucher_type: "Payment",
    debit_ledgers: "Provident Fund Payable (₹1,000)",
    credit_ledgers: "Axis Bank – Current Account (₹1,000)",
    narration_example: "PF remittance",
    notes: "Standalone 1000 remittance example",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | PF Payable | 1,000 | \nAsset | Bank A/c |  | 1,000"
  },
  {
    scenario_category: "Payroll – Contractors",
    scenario: "Professional Tax payment to State authority",
    ledger_name: "Professional Tax Payable",
    tally_group: "Duties & Taxes",
    ledger_code: "220.905",
    voucher_type: "Payment",
    debit_ledgers: "Professional Tax Payable (₹1,000)",
    credit_ledgers: "Axis Bank – Current Account (₹1,000)",
    narration_example: "PT remittance",
    notes: "Standalone 1000 remittance example",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nLiability | Professional Tax Payable | 1,000 | \nAsset | Bank A/c |  | 1,000"
  },
  {
    scenario_category: "Export / Zero-rated",
    scenario: "Export receipt received partially (50% of ₹1,000 invoice)",
    ledger_name: "Customers – Export",
    tally_group: "Sundry Debtors",
    ledger_code: "120.9",
    voucher_type: "Receipt",
    debit_ledgers: "Axis Bank – Current Account (₹500)",
    credit_ledgers: "Customers – Export (₹500)",
    narration_example: "Partial export receipt",
    notes: "Remaining 500 outstanding",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nAsset | Customer – Export Debtors | 1,000 | \nIncome | Export Sales / Service Income |  | 1,000"
  },
  {
    scenario_category: "Payroll – Contractors",
    scenario: "Salary payment with TDS deduction (professional / consultant payroll)",
    ledger_name: "Professional Fees / Contract Staff",
    tally_group: "Direct Expenses",
    ledger_code: "510.95",
    voucher_type: "Payment",
    debit_ledgers: "Professional Fees / Contract Staff (₹1,000)",
    credit_ledgers: "Bank (₹900) + TDS Payable (₹100)",
    narration_example: "Payment after TDS deduction",
    notes: "Payroll / Statutory – TDS deducted at 10%",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Professional Fees / Salary Expense | 1,000 | \nLiability | TDS Payable @10% |  | 100\nAsset | Bank A/c (Net Paid) |  | 900"
  },
  {
    scenario_category: "Payroll – Contractors",
    scenario: "Contractor invoice with GST (GST + TDS applicable)",
    ledger_name: "Professional Fees – Contractor",
    tally_group: "Indirect Expenses",
    ledger_code: "610.951",
    voucher_type: "Purchase",
    debit_ledgers: "Professional Fees + Input GST",
    credit_ledgers: "Vendor – Contractor",
    narration_example: "Contractor bill with GST",
    notes: "Contractor registered under GST; ITC available; TDS applies at payment stage",
    examples: "Account Type | Account Name | Debit (₹) | Credit (₹)\nExpense | Professional Fees (Base) | 1,000 | \nAsset | Input CGST @9% | 90 | \nAsset | Input SGST @9% | 90 | \nLiability | Vendor – Contractor |  | 1,180"
  }
];

const VOUCHER_TYPES = ['Receipt', 'Payment', 'Journal', 'Purchase', 'Sales', 'Contra', 'Credit Note'];
const SCENARIO_CATEGORIES = [
  'Receipts', 'Payments', 'Sales', 'Purchases', 'GST', 'TDS',
  'Bank / Cash Transfers', 'Bank Charges', 'Adjustments / Provisions',
  'General', 'Export / Zero-rated', 'Forex / Exchange Difference',
  'Payroll \u2013 Employees', 'Payroll \u2013 Contractors'
];
const TALLY_GROUPS = [
  'Capital Account', 'Loans (Liability)', 'Sundry Creditors', 'Duties & Taxes',
  'Bank Accounts', 'Cash-in-Hand', 'Sundry Debtors', 'Current Assets', 'Fixed Assets',
  'Direct Income', 'Indirect Income', 'Direct Expenses', 'Indirect Expenses',
  'Current Liabilities'
];

const VOUCHER_COLORS = {
  Receipt: '#10b981', Payment: '#ef4444', Journal: '#8b5cf6',
  Purchase: '#f59e0b', Sales: '#3b82f6', Contra: '#64748b', 'Credit Note': '#ec4899'
};
const CAT_COLORS = {
  Receipts: '#10b981', Payments: '#ef4444', Sales: '#3b82f6', Purchases: '#f59e0b',
  GST: '#06b6d4', TDS: '#8b5cf6', 'Bank / Cash Transfers': '#64748b',
  'Bank Charges': '#94a3b8', 'Adjustments / Provisions': '#a78bfa',
  General: '#6b7280', 'Export / Zero-rated': '#14b8a6',
  'Forex / Exchange Difference': '#0ea5e9',
  'Payroll \u2013 Employees': '#f97316', 'Payroll \u2013 Contractors': '#d946ef'
};
const GROUP_TYPE_MAP = {
  'Capital Account': 'Equity', 'Loans (Liability)': 'Liability', 'Sundry Creditors': 'Liability',
  'Duties & Taxes': 'Liability', 'Bank Accounts': 'Asset', 'Cash-in-Hand': 'Asset',
  'Sundry Debtors': 'Asset', 'Current Assets': 'Asset', 'Fixed Assets': 'Asset',
  'Direct Income': 'Revenue', 'Indirect Income': 'Revenue',
  'Direct Expenses': 'Expense', 'Indirect Expenses': 'Expense',
  'Current Liabilities': 'Liability',
};
const TYPE_COLORS = { Asset: '#10b981', Liability: '#ef4444', Equity: '#8b5cf6', Revenue: '#3b82f6', Expense: '#f59e0b' };

const EMPTY_ENTRY = {
  scenario_category: '', scenario: '', ledger_name: '', tally_group: '', ledger_code: '',
  voucher_type: '', debit_ledgers: '', credit_ledgers: '', narration_example: '', notes: '', examples: ''
};

/* Parse examples string into table rows */
function parseExamples(str) {
  if (!str) return null;
  const lines = str.split('\n').filter(l => l.trim());
  if (lines.length < 2) return null;
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split('|').map(c => c.trim());
    if (cols.length >= 4) {
      rows.push({ type: cols[0], name: cols[1], debit: cols[2], credit: cols[3] });
    }
  }
  return rows.length > 0 ? rows : null;
}

function ExampleTable({ examples }) {
  const rows = parseExamples(examples);
  if (!rows) return null;
  return (
    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', marginTop: 4 }}>
      <thead>
        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
          <th style={{ textAlign: 'left', padding: '2px 6px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Type</th>
          <th style={{ textAlign: 'left', padding: '2px 6px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Account</th>
          <th style={{ textAlign: 'right', padding: '2px 6px', color: '#10b981', fontWeight: 500 }}>Dr</th>
          <th style={{ textAlign: 'right', padding: '2px 6px', color: '#ef4444', fontWeight: 500 }}>Cr</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <td style={{ padding: '2px 6px', color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{r.type}</td>
            <td style={{ padding: '2px 6px', color: 'rgba(255,255,255,0.7)' }}>{r.name}</td>
            <td style={{ textAlign: 'right', padding: '2px 6px', color: r.debit ? '#10b981' : 'transparent', fontFamily: 'monospace' }}>{r.debit || '\u2014'}</td>
            <td style={{ textAlign: 'right', padding: '2px 6px', color: r.credit ? '#ef4444' : 'transparent', fontFamily: 'monospace' }}>{r.credit || '\u2014'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AccountingGuide() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_ENTRY });
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [filterGroup, setFilterGroup] = useState('');
  const [filterVoucher, setFilterVoucher] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [expandedExamples, setExpandedExamples] = useState({});

  const fetchRecords = useCallback(async () => {
    try {
      const res = await apiFetch('/api/finance/accounting-guide');
      if (res.ok) {
        const data = await res.json();
        setRecords(data.records || []);
      }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  function handleField(name, value) {
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  }

  function validate() {
    const errs = {};
    if (!form.scenario.trim()) errs.scenario = 'Scenario is required';
    if (!form.ledger_name.trim()) errs.ledger_name = 'Ledger name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/finance/accounting-guide/${editing.id}` : '/api/finance/accounting-guide';
      const res = await apiFetch(url, { method, body: JSON.stringify(form) });
      if (res.ok) {
        setModalOpen(false);
        setEditing(null);
        setForm({ ...EMPTY_ENTRY });
        fetchRecords();
      }
    } catch (e) { /* handled */ }
    setSaving(false);
  }

  function openEdit(rec) {
    setEditing(rec);
    setForm({
      scenario_category: rec.scenario_category || '', scenario: rec.scenario || '',
      ledger_name: rec.ledger_name || '', tally_group: rec.tally_group || '',
      ledger_code: rec.ledger_code || '', voucher_type: rec.voucher_type || '',
      debit_ledgers: rec.debit_ledgers || '', credit_ledgers: rec.credit_ledgers || '',
      narration_example: rec.narration_example || '', notes: rec.notes || '',
      examples: rec.examples || ''
    });
    setErrors({});
    setModalOpen(true);
  }

  async function handleDelete(rec) {
    if (!confirm(`Delete "${rec.scenario}" entry?`)) return;
    await apiFetch(`/api/finance/accounting-guide/${rec.id}`, { method: 'DELETE' });
    fetchRecords();
  }

  async function importTemplate() {
    if (!confirm('Import all 64 Tally LLP voucher templates? This will replace existing records.')) return;
    setImporting(true);
    setImportMsg('');
    try {
      const res = await apiFetch('/api/finance/accounting-guide/import', {
        method: 'POST',
        body: JSON.stringify({ records: TALLY_GUIDE_DATA })
      });
      if (res.ok) {
        const data = await res.json();
        setImportMsg(`${data.imported} records imported`);
        fetchRecords();
      } else { setImportMsg('Import failed'); }
    } catch (e) { setImportMsg('Import error'); }
    setImporting(false);
    setTimeout(() => setImportMsg(''), 4000);
  }

  function toggleGroup(key) {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleExample(id) {
    setExpandedExamples(prev => ({ ...prev, [id]: !prev[id] }));
  }

  // Filter
  const filtered = useMemo(() => {
    let data = records;
    if (filterGroup) data = data.filter(r => r.tally_group === filterGroup);
    if (filterVoucher) data = data.filter(r => r.voucher_type === filterVoucher);
    if (filterCategory) data = data.filter(r => r.scenario_category === filterCategory);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      data = data.filter(r =>
        (r.scenario || '').toLowerCase().includes(q) ||
        (r.ledger_name || '').toLowerCase().includes(q) ||
        (r.debit_ledgers || '').toLowerCase().includes(q) ||
        (r.credit_ledgers || '').toLowerCase().includes(q) ||
        (r.notes || '').toLowerCase().includes(q) ||
        (r.examples || '').toLowerCase().includes(q)
      );
    }
    return data;
  }, [records, filterGroup, filterVoucher, filterCategory, filterSearch]);

  // Group by tally_group
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(r => {
      const key = r.tally_group || 'Other';
      if (!g[key]) g[key] = [];
      g[key].push(r);
    });
    return g;
  }, [filtered]);

  const groupKeys = useMemo(() => {
    return Object.keys(grouped).sort((a, b) => {
      const ia = TALLY_GROUPS.indexOf(a), ib = TALLY_GROUPS.indexOf(b);
      if (ia >= 0 && ib >= 0) return ia - ib;
      if (ia >= 0) return -1;
      if (ib >= 0) return 1;
      return a.localeCompare(b);
    });
  }, [grouped]);

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, marginTop: -40, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="form-control" placeholder="Search..." value={filterSearch} onChange={e => setFilterSearch(e.target.value)} style={{ width: 180 }} />
          {/* Modern, search-enabled dropdowns. SearchableSelect ships its own
              filter input inside the popup so the toolbar stays compact. */}
          <div style={{ width: 180 }}>
            <SearchableSelect
              value={filterCategory}
              onChange={setFilterCategory}
              options={[{ value: '', label: 'All Categories' }, ...SCENARIO_CATEGORIES.map(c => ({ value: c, label: c }))]}
              placeholder="All Categories"
              sort={false}
            />
          </div>
          <div style={{ width: 170 }}>
            <SearchableSelect
              value={filterGroup}
              onChange={setFilterGroup}
              options={[{ value: '', label: 'All Groups' }, ...TALLY_GROUPS.map(g => ({ value: g, label: g }))]}
              placeholder="All Groups"
              sort={false}
            />
          </div>
          <div style={{ width: 150 }}>
            <SearchableSelect
              value={filterVoucher}
              onChange={setFilterVoucher}
              options={[{ value: '', label: 'All Vouchers' }, ...VOUCHER_TYPES.map(v => ({ value: v, label: v }))]}
              placeholder="All Vouchers"
              sort={false}
            />
          </div>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{filtered.length} of {records.length}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {importMsg && <span style={{ fontSize: 12, color: '#10b981', fontWeight: 500 }}>{importMsg}</span>}
          <button className="btn btn-secondary" onClick={importTemplate} disabled={importing}>
            {importing ? 'Importing...' : 'Load Tally LLP Template'}
          </button>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setForm({ ...EMPTY_ENTRY }); setErrors({}); setModalOpen(true); }}>
            + New Entry
          </button>
        </div>
      </div>

      {/* Empty State */}
      {loading ? <p style={{ textAlign: 'center', padding: 40 }}>Loading...</p> : filtered.length === 0 && records.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'rgba(255,255,255,0.4)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'\u{1F4D6}'}</div>
          <p style={{ fontSize: 16, marginBottom: 16 }}>No accounting guide entries yet.</p>
          <button className="btn btn-primary" onClick={importTemplate} disabled={importing}>
            Load Tally LLP Voucher Templates (64 entries)
          </button>
        </div>
      ) : (
        /* Grouped Cards */
        groupKeys.map(groupName => {
          const recs = grouped[groupName];
          const acctType = GROUP_TYPE_MAP[groupName] || '';
          const collapsed = collapsedGroups[groupName];
          const uniqueLedgers = [...new Set(recs.map(r => r.ledger_code).filter(Boolean))];

          return (
            <div key={groupName} className="card" style={{ marginBottom: 8 }}>
              <div className="card-header" style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => toggleGroup(groupName)}>
                <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)' }}>{'\u25BC'}</span>
                <h4 style={{ flex: 1, margin: 0 }}>{groupName}</h4>
                {acctType && <span className="badge" style={{ background: TYPE_COLORS[acctType] || '#64748b', color: '#fff', fontSize: 10 }}>{acctType}</span>}
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{uniqueLedgers.length} ledger{uniqueLedgers.length !== 1 ? 's' : ''} \u00B7 {recs.length} entr{recs.length !== 1 ? 'ies' : 'y'}</span>
              </div>
              {!collapsed && (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ minWidth: 1000 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 110 }}>Category</th>
                        <th style={{ width: 60 }}>Code</th>
                        <th style={{ width: 200 }}>Scenario</th>
                        <th style={{ width: 160 }}>Ledger Name</th>
                        <th style={{ width: 75 }}>Voucher</th>
                        <th>Debit Ledger(s)</th>
                        <th>Credit Ledger(s)</th>
                        <th style={{ width: 110 }}>Narration</th>
                        <th style={{ width: 160 }}>Notes</th>
                        <th style={{ width: 40 }}>Ex.</th>
                        <th style={{ width: 70 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recs.map(r => {
                        const hasEx = r.examples && r.examples.trim();
                        const exOpen = expandedExamples[r.id];
                        return (
                          <React.Fragment key={r.id}>
                            <tr>
                              <td>
                                <span className="badge" style={{ background: CAT_COLORS[r.scenario_category] || '#64748b', color: '#fff', fontSize: 9, whiteSpace: 'nowrap' }}>
                                  {r.scenario_category || '\u2014'}
                                </span>
                              </td>
                              <td style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: TYPE_COLORS[acctType] || '#e2e8f0' }}>{r.ledger_code || '\u2014'}</td>
                              <td style={{ fontSize: 12 }}>{r.scenario}</td>
                              <td style={{ fontSize: 12 }}>{r.ledger_name}</td>
                              <td>
                                <span className="badge" style={{ background: VOUCHER_COLORS[r.voucher_type] || '#64748b', color: '#fff', fontSize: 9, whiteSpace: 'nowrap' }}>
                                  {r.voucher_type || '\u2014'}
                                </span>
                              </td>
                              <td style={{ fontSize: 11, color: '#10b981' }}>{r.debit_ledgers || '\u2014'}</td>
                              <td style={{ fontSize: 11, color: '#ef4444' }}>{r.credit_ledgers || '\u2014'}</td>
                              <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>{r.narration_example || '\u2014'}</td>
                              <td style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{r.notes || '\u2014'}</td>
                              <td style={{ textAlign: 'center' }}>
                                {hasEx && (
                                  <button
                                    className="btn btn-sm"
                                    style={{ background: exOpen ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 10, padding: '2px 6px', color: exOpen ? '#3b82f6' : 'rgba(255,255,255,0.5)' }}
                                    onClick={() => toggleExample(r.id)}
                                    title="Show journal entry example"
                                  >
                                    {'\u{1F4CB}'}
                                  </button>
                                )}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {/* Replaced cryptic "Ed"/"X" labels with clear pencil + trash icons. The buttons sit
                                    side-by-side so the action column stays compact. */}
                                <button
                                  className="btn btn-sm"
                                  onClick={() => openEdit(r)}
                                  title="Edit entry"
                                  style={{ marginRight: 4, fontSize: 11, padding: '3px 8px',
                                           background: 'rgba(59,130,246,0.15)', color: '#3B82F6',
                                           border: '1px solid rgba(59,130,246,0.35)', borderRadius: 4 }}>
                                  ✏ Edit
                                </button>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => handleDelete(r)}
                                  title="Delete entry"
                                  style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444',
                                           border: '1px solid rgba(239,68,68,0.35)', fontSize: 11,
                                           padding: '3px 8px', borderRadius: 4 }}>
                                  🗑 Delete
                                </button>
                              </td>
                            </tr>
                            {exOpen && hasEx && (
                              <tr>
                                <td colSpan={11} style={{ padding: '4px 16px 8px 60px', background: 'rgba(59,130,246,0.04)', borderTop: 'none' }}>
                                  <ExampleTable examples={r.examples} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Create/Edit Modal */}
      <Modal
        title={editing ? 'Edit Accounting Guide Entry' : 'New Accounting Guide Entry'}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        size="lg"
      >
        {errors._general && (
          <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
            {errors._general}
          </div>
        )}
        <div className="form-row">
          <FormField label="Scenario Category" name="scenario_category" type="select" value={form.scenario_category} onChange={handleField} options={SCENARIO_CATEGORIES} placeholder="Select Category..." />
          <FormField label="Voucher Type" name="voucher_type" type="select" value={form.voucher_type} onChange={handleField} options={VOUCHER_TYPES} placeholder="Select Type..." />
        </div>
        <div className="form-row">
          <FormField label="Scenario (When to use)" name="scenario" value={form.scenario} onChange={handleField} error={errors.scenario} required />
          <FormField label="Ledger Name" name="ledger_name" value={form.ledger_name} onChange={handleField} error={errors.ledger_name} required />
        </div>
        <div className="form-row">
          <FormField label="Tally Group" name="tally_group" type="select" value={form.tally_group} onChange={handleField} options={TALLY_GROUPS} placeholder="Select Group..." />
          <FormField label="Ledger Code" name="ledger_code" value={form.ledger_code} onChange={handleField} placeholder="e.g. 300.001" />
        </div>
        <div className="form-row">
          <FormField label="Debit Ledger(s)" name="debit_ledgers" value={form.debit_ledgers} onChange={handleField} />
          <FormField label="Credit Ledger(s)" name="credit_ledgers" value={form.credit_ledgers} onChange={handleField} />
        </div>
        <FormField label="Narration (example)" name="narration_example" value={form.narration_example} onChange={handleField} />
        <FormField label="Notes (GST / TDS / Export-Forex)" name="notes" type="textarea" value={form.notes} onChange={handleField} />
        <FormField label="Examples (pipe-delimited: Type | Account | Debit | Credit, one per line)" name="examples" type="textarea" value={form.examples} onChange={handleField} />

        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : (editing ? 'Update Entry' : 'Create Entry')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
